/**
 * WebSocket Client for Real-Time Notifications
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Connection state tracking
 * - Heartbeat/ping-pong for connection health
 * - Event dispatching to subscribers
 */

import { config } from '../../config/env'
import type { ActiveNotificationChannel } from '@rondela/shared'

// WebSocket message types (must match backend)
export type WebSocketMessageType =
  | 'connected'
  | 'heartbeat'
  | 'dose_reminder'
  | 'dose_update'
  | 'notification'
  | 'error'

export interface WebSocketMessage {
  type: WebSocketMessageType
  payload?: unknown
  timestamp: string
}

export interface DoseReminderPayload {
  doseId: string
  medicationId: string
  medicationNombre: string
  petNombre: string
  scheduledFor: string
}

export interface DoseUpdatePayload {
  doseId: string
  status: 'taken' | 'skipped' | 'overdue'
  medicationNombre: string
  petNombre: string
  takenByNombre?: string
}

export interface NotificationPayload {
  title: string
  body: string
  data?: Record<string, unknown>
}

type MessageHandler = (message: WebSocketMessage) => void
type ConnectionHandler = (state: ConnectionState) => void

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting'

interface WebSocketClientOptions {
  groupId: string
  userId: string
  onMessage?: MessageHandler
  onConnectionChange?: ConnectionHandler
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000] // Max 30s between retries
const HEARTBEAT_INTERVAL = 30000 // 30 seconds
const HEARTBEAT_TIMEOUT = 10000 // 10 seconds to receive pong

export class WebSocketClient {
  private ws: WebSocket | null = null
  private groupId: string
  private userId: string
  private messageHandlers: Set<MessageHandler> = new Set()
  private connectionHandlers: Set<ConnectionHandler> = new Set()
  private reconnectAttempts = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null
  private connectionState: ConnectionState = 'disconnected'
  private isIntentionallyClosed = false

  constructor(options: WebSocketClientOptions) {
    this.groupId = options.groupId
    this.userId = options.userId
    if (options.onMessage) {
      this.messageHandlers.add(options.onMessage)
    }
    if (options.onConnectionChange) {
      this.connectionHandlers.add(options.onConnectionChange)
    }
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return // Already connected
    }

    this.isIntentionallyClosed = false
    this.setConnectionState('connecting')

    try {
      const wsUrl = this.buildWebSocketUrl()
      console.log('[WebSocket] Connecting to:', wsUrl)

      this.ws = new WebSocket(wsUrl)
      this.setupEventHandlers()
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error)
      this.scheduleReconnect()
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true
    this.clearTimers()

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }

    this.setConnectionState('disconnected')
  }

  /**
   * Add a message handler
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler)
    return () => this.messageHandlers.delete(handler)
  }

  /**
   * Add a connection state handler
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler)
    // Immediately call with current state
    handler(this.connectionState)
    return () => this.connectionHandlers.delete(handler)
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState
  }

  /**
   * Get the active notification channel type
   */
  getActiveChannel(): ActiveNotificationChannel {
    switch (this.connectionState) {
      case 'connected':
        return 'websocket'
      case 'connecting':
      case 'reconnecting':
        return 'polling' // Fallback while connecting
      case 'disconnected':
        return 'disconnected'
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN
  }

  private buildWebSocketUrl(): string {
    // Convert HTTP URL to WebSocket URL
    const apiUrl = config.apiUrl || window.location.origin
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws'
    const baseUrl = apiUrl.replace(/^https?/, wsProtocol)

    return `${baseUrl}/api/notifications/ws/${this.groupId}?userId=${encodeURIComponent(this.userId)}`
  }

  private setupEventHandlers(): void {
    if (!this.ws) return

    this.ws.onopen = () => {
      console.log('[WebSocket] Connected')
      this.reconnectAttempts = 0
      this.setConnectionState('connected')
      this.startHeartbeat()
    }

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data)
        this.handleMessage(message)
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error)
      }
    }

    this.ws.onerror = (event) => {
      console.error('[WebSocket] Error:', event)
    }

    this.ws.onclose = (event) => {
      console.log(`[WebSocket] Closed: code=${event.code}, reason=${event.reason}`)
      this.stopHeartbeat()

      if (!this.isIntentionallyClosed) {
        this.scheduleReconnect()
      } else {
        this.setConnectionState('disconnected')
      }
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    // Handle heartbeat internally
    if (message.type === 'heartbeat') {
      this.clearHeartbeatTimeout()
      return
    }

    // Dispatch to handlers
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        console.error('[WebSocket] Handler error:', error)
      }
    })
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return

    this.connectionState = state
    this.connectionHandlers.forEach((handler) => {
      try {
        handler(state)
      } catch (error) {
        console.error('[WebSocket] Connection handler error:', error)
      }
    })
  }

  private scheduleReconnect(): void {
    if (this.isIntentionallyClosed) return

    this.setConnectionState('reconnecting')

    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempts, RECONNECT_DELAYS.length - 1)]
    console.log(`[WebSocket] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`)

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++
      this.connect()
    }, delay)
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()

    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Send ping
        this.ws.send(JSON.stringify({ type: 'ping' }))

        // Start timeout for pong
        this.heartbeatTimeout = setTimeout(() => {
          console.warn('[WebSocket] Heartbeat timeout, reconnecting...')
          this.ws?.close(4000, 'Heartbeat timeout')
        }, HEARTBEAT_TIMEOUT)
      }
    }, HEARTBEAT_INTERVAL)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    this.clearHeartbeatTimeout()
  }

  private clearHeartbeatTimeout(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout)
      this.heartbeatTimeout = null
    }
  }

  private clearTimers(): void {
    this.stopHeartbeat()
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }
}

// Singleton instance for the app
let clientInstance: WebSocketClient | null = null

export function getWebSocketClient(): WebSocketClient | null {
  return clientInstance
}

export function createWebSocketClient(options: WebSocketClientOptions): WebSocketClient {
  if (clientInstance) {
    clientInstance.disconnect()
  }
  clientInstance = new WebSocketClient(options)
  return clientInstance
}

export function destroyWebSocketClient(): void {
  if (clientInstance) {
    clientInstance.disconnect()
    clientInstance = null
  }
}
